import { useState } from "react";
import { LifeBuoy, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/hooks/useBranding";
import { errMsg } from "@/lib/api";

/**
 * Persistent strip shown to a Landlord (super-admin) while they are working inside another
 * organisation as BuiltOS support. Everything they do in this state is stamped
 * "(BuiltOS support)" by the API; the strip makes that state impossible to miss and offers
 * a one-click way home.
 */
export const ActingBanner = () => {
  const { user, switchOrg } = useAuth();
  const { shortName, name } = useBranding();
  const [busy, setBusy] = useState(false);

  if (!user?.is_superadmin || !user?.acting_org_id) return null;

  const goHome = async () => {
    setBusy(true);
    try {
      await switchOrg(null);
    } catch (e) {
      toast.error(errMsg(e, "Could not switch back — please try again"));
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      className="z-[45] flex items-center gap-3 border-b border-primary/40 bg-accent px-4 py-2 text-accent-foreground lg:sticky lg:top-0 sm:px-6 lg:px-8"
      data-testid="acting-org-banner"
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary">
        <LifeBuoy className="h-4 w-4" />
      </span>
      <p className="min-w-0 flex-1 truncate text-sm">
        <span className="font-semibold">BuiltOS support mode</span>
        <span className="text-accent-foreground/80"> — you are working inside </span>
        <span className="font-semibold" data-testid="acting-org-banner-name">
          {shortName || name || "this organisation"}
        </span>
        <span className="hidden text-accent-foreground/80 sm:inline"> as their admin. Every change is logged as BuiltOS support.</span>
      </p>
      <Button
        size="sm"
        variant="outline"
        className="h-8 shrink-0 border-primary/40 bg-background/60 hover:bg-background"
        onClick={goHome}
        disabled={busy}
        data-testid="acting-org-banner-return-btn"
      >
        {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Undo2 className="mr-1.5 h-3.5 w-3.5" />}
        Return home
      </Button>
    </div>
  );
};
